import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Beef, Wheat, Flame, Target, Scale } from "lucide-react";

/* ---------- Interactive rotating wheel ---------- */

const WHEEL = [
  {
    key: "protein",
    label: "Protein",
    hint: "Build & repair",
    fill: "#031D47",
    text: "#F4F3EF",
    icon: Beef,
    // SVG geometry — three equal 120° wedges starting at the top (−90°)
    startDeg: -90,
    endDeg: 30,
    labelDeg: -30,
  },
  {
    key: "carbs",
    label: "Carbs",
    hint: "Your fuel",
    fill: "#4E5B43",
    text: "#F4F3EF",
    icon: Wheat,
    startDeg: 30,
    endDeg: 150,
    labelDeg: 90,
  },
  {
    key: "fat",
    label: "Fat",
    hint: "Essential",
    fill: "#B7AA8D",
    text: "#031D47",
    icon: Flame,
    startDeg: 150,
    endDeg: 270,
    labelDeg: 210,
  },
];

function wedgePath(cx, cy, r, startDeg, endDeg) {
  const s = (startDeg * Math.PI) / 180;
  const e = (endDeg * Math.PI) / 180;
  const x1 = cx + r * Math.cos(s);
  const y1 = cy + r * Math.sin(s);
  const x2 = cx + r * Math.cos(e);
  const y2 = cy + r * Math.sin(e);
  const large = endDeg - startDeg > 180 ? 1 : 0;
  return `M ${cx} ${cy} L ${x1.toFixed(2)} ${y1.toFixed(2)} A ${r} ${r} 0 ${large} 1 ${x2.toFixed(2)} ${y2.toFixed(2)} Z`;
}

function MacrosWheel({ onPick }) {
  const CX = 150;
  const CY = 150;
  const R = 148;
  const LABEL_R = 92;

  return (
    <div className="dta-wheel relative w-[280px] h-[280px] md:w-[360px] md:h-[360px] mx-auto">
      <svg
        viewBox="0 0 300 300"
        className="dta-wheel-spin w-full h-full"
        role="img"
        aria-label="Macros wheel — click a segment to learn more"
      >
        <defs>
          <filter id="softShadow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="4" />
          </filter>
        </defs>
        {/* subtle outer ring */}
        <circle cx={CX} cy={CY} r={R + 1} fill="none" stroke="#0A0A0A" strokeOpacity="0.08" />

        {WHEEL.map((w) => {
          const d = wedgePath(CX, CY, R, w.startDeg, w.endDeg);
          const lx = CX + LABEL_R * Math.cos((w.labelDeg * Math.PI) / 180);
          const ly = CY + LABEL_R * Math.sin((w.labelDeg * Math.PI) / 180);
          return (
            <g
              key={w.key}
              style={{ cursor: "pointer" }}
              onClick={() => onPick(w.key)}
              data-testid={`macros-wheel-${w.key}`}
            >
              <path d={d} fill={w.fill} />
              <path
                d={d}
                fill="transparent"
                stroke="#F4F3EF"
                strokeWidth="1.5"
                strokeOpacity="0.9"
              />
              <text
                x={lx}
                y={ly}
                textAnchor="middle"
                dominantBaseline="middle"
                fill={w.text}
                className="font-display"
                style={{
                  fontWeight: 800,
                  fontSize: "22px",
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                  pointerEvents: "none",
                }}
              >
                {w.label}
              </text>
              <text
                x={lx}
                y={ly + 18}
                textAnchor="middle"
                dominantBaseline="middle"
                fill={w.text}
                opacity="0.75"
                style={{
                  fontSize: "9px",
                  letterSpacing: "0.22em",
                  textTransform: "uppercase",
                  pointerEvents: "none",
                }}
              >
                {w.hint}
              </text>
            </g>
          );
        })}

        {/* centre hub */}
        <circle cx={CX} cy={CY} r="34" fill="#F4F3EF" />
        <circle cx={CX} cy={CY} r="34" fill="none" stroke="#031D47" strokeOpacity="0.15" />
        <text
          x={CX}
          y={CY - 4}
          textAnchor="middle"
          dominantBaseline="middle"
          fill="#031D47"
          className="font-display"
          style={{
            fontWeight: 800,
            fontSize: "14px",
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            pointerEvents: "none",
          }}
        >
          DTA
        </text>
        <text
          x={CX}
          y={CY + 10}
          textAnchor="middle"
          dominantBaseline="middle"
          fill="#031D47"
          opacity="0.7"
          style={{
            fontSize: "7px",
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            pointerEvents: "none",
          }}
        >
          Macros
        </text>
      </svg>

      {/* a11y: non-visual button list that drives the same navigation */}
      <ul className="sr-only">
        {WHEEL.map((w) => (
          <li key={w.key}>
            <button type="button" onClick={() => onPick(w.key)}>
              Jump to {w.label}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ---------- Simple vs Science content blocks ---------- */

const SIMPLE_CARDS = [
  {
    key: "protein",
    icon: Beef,
    overline: "Protein",
    title: "Build & repair",
    body: "Protein helps your body maintain, repair and build muscle.",
    examples: [
      "Chicken",
      "Beef",
      "Fish",
      "Eggs",
      "Greek yoghurt",
      "Cottage cheese",
      "Beans",
      "Protein shakes",
    ],
    footer:
      "If building or maintaining muscle is part of your goal, getting enough protein is particularly important.",
  },
  {
    key: "carbs",
    icon: Wheat,
    overline: "Carbohydrates",
    title: "Your fuel",
    body:
      "Carbohydrates are one of your body's main sources of energy. They help fuel training, walking, work, sport and everyday activity.",
    examples: ["Rice", "Potatoes", "Pasta", "Oats", "Bread", "Fruit", "Vegetables"],
    footer:
      "Carbohydrates aren't automatically something you need to avoid. The amount you eat matters.",
  },
  {
    key: "fat",
    icon: Flame,
    overline: "Fat",
    title: "Your body needs it",
    body:
      "Dietary fat is an important part of a balanced diet and supports a number of normal functions within the body.",
    examples: ["Olive oil", "Nuts", "Seeds", "Avocado", "Eggs", "Oily fish", "Dairy"],
    footer: "Fat isn't the enemy. The key is getting the right amount for your overall calorie target.",
  },
];

function SimpleContent() {
  return (
    <div className="space-y-16" data-testid="macros-content-simple">
      {/* Food budget intro */}
      <div className="max-w-3xl">
        <p className="dta-overline text-navy">Keep it simple</p>
        <h3 className="font-display uppercase font-bold text-3xl md:text-5xl mt-3 leading-[0.95]">
          Macros in plain English.
        </h3>
        <p className="mt-6 text-[#272727] text-base md:text-lg leading-relaxed">
          Think of your daily macros as your food budget. DTA works out approximately how much food energy your body needs each day and then divides that allowance between{" "}
          <strong className="text-[#0A0A0A]">protein, carbohydrates and fat</strong>.
        </p>
      </div>

      {/* 3 macro cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {SIMPLE_CARDS.map((c) => {
          const Icon = c.icon;
          return (
            <article
              key={c.key}
              id={`macros-${c.key}`}
              data-testid={`macros-card-simple-${c.key}`}
              className="bg-white border border-black/10 p-7 md:p-8 flex flex-col scroll-mt-28"
            >
              <div className="flex items-center gap-3">
                <Icon className="w-5 h-5 text-[#031D47]" strokeWidth={1.5} />
                <p className="dta-overline text-navy">{c.overline}</p>
              </div>
              <h4 className="font-display uppercase font-bold text-2xl md:text-3xl mt-3 leading-tight">
                {c.title}
              </h4>
              <p className="mt-4 text-sm md:text-[0.95rem] text-[#272727] leading-relaxed">
                {c.body}
              </p>
              <div className="mt-5">
                <p className="dta-overline text-[0.65rem] text-[#272727]/60">Examples</p>
                <ul className="mt-3 flex flex-wrap gap-2">
                  {c.examples.map((e) => (
                    <li
                      key={e}
                      className="border border-black/15 px-2.5 py-1 text-xs text-[#272727]"
                    >
                      {e}
                    </li>
                  ))}
                </ul>
              </div>
              <p className="mt-6 pt-5 border-t border-black/10 text-sm text-[#272727]/85 italic leading-relaxed">
                {c.footer}
              </p>
            </article>
          );
        })}
      </div>

      {/* So what does DTA do? */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        <div className="lg:col-span-7">
          <p className="dta-overline text-navy">The maths</p>
          <h3 className="font-display uppercase font-bold text-3xl md:text-5xl mt-3 leading-[0.95]">
            So what does DTA do?
          </h3>
          <p className="mt-6 text-[#272727] text-base md:text-lg leading-relaxed">
            Instead of expecting you to work all of this out yourself, the DTA Macro Calculator does the maths for you. You tell us your age, height, weight, activity level and goal. DTA then gives you a daily target for calories, protein, carbohydrates and fat.
          </p>
        </div>
        <div className="lg:col-span-5">
          <div className="bg-[#031D47] text-white p-7 md:p-8 border border-[#031D47]">
            <p className="dta-overline text-white/60">Example daily target</p>
            <ul className="mt-6 space-y-4">
              {[
                { v: "2,200", u: "Calories" },
                { v: "180g", u: "Protein" },
                { v: "200g", u: "Carbohydrates" },
                { v: "75g", u: "Fat" },
              ].map((r) => (
                <li
                  key={r.u}
                  className="flex items-baseline justify-between border-b border-white/15 pb-3 last:border-0"
                >
                  <span className="font-display uppercase text-3xl md:text-4xl font-bold">
                    {r.v}
                  </span>
                  <span className="dta-overline text-white/70 text-[0.65rem]">{r.u}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Key message */}
      <div className="bg-[#0A0A0A] text-white p-8 md:p-14 grain">
        <p className="dta-overline text-white/50">Key message</p>
        <h3 className="hero-headline text-4xl md:text-6xl mt-4 leading-[0.95]">
          No guesswork.<br />Just a target.
        </h3>
        <div className="mt-8 max-w-2xl space-y-4 text-white/75 text-base md:text-lg leading-relaxed">
          <p>
            You don't have to hit every number perfectly every single day. What matters is consistency over time.
          </p>
          <p>
            If your calorie and macro targets are appropriate for your goal and you follow your plan consistently — alongside suitable activity, training, sleep and recovery — you should be able to measure whether you are moving in the right direction.
          </p>
        </div>
      </div>

      {/* DTA philosophy mini-pathway */}
      <div>
        <p className="dta-overline text-navy">The DTA philosophy</p>
        <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { k: "Down", b: "Establish where you are now." },
            { k: "Test", b: "Follow the plan and measure what happens." },
            {
              k: "Adjust",
              b: "If the results aren't moving in the direction you want, make changes based on the evidence.",
            },
          ].map((s, i) => (
            <div key={s.k} className="border-t-2 border-[#031D47] pt-5">
              <span className="dta-overline text-[0.65rem] text-navy">
                {String(i + 1).padStart(2, "0")}
              </span>
              <h4 className="font-display uppercase text-2xl md:text-3xl font-bold mt-2">
                {s.k}
              </h4>
              <p className="mt-3 text-sm text-[#272727] leading-relaxed">{s.b}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const SCIENCE_CARDS = [
  {
    key: "protein",
    icon: Beef,
    overline: "Protein",
    kcal: "4 kcal / g",
    body:
      "Protein consists of amino acids that are involved in the growth, repair and maintenance of body tissues.",
    note:
      "Adequate dietary protein is particularly important when resistance training and when trying to preserve lean body mass during a calorie deficit.",
  },
  {
    key: "carbs",
    icon: Wheat,
    overline: "Carbohydrates",
    kcal: "4 kcal / g",
    body:
      "Carbohydrates are broken down predominantly into glucose, which can be used for energy or stored as glycogen in the liver and muscles.",
    note:
      "Carbohydrate intake can be particularly useful in supporting exercise performance and replenishing glycogen following physical activity.",
  },
  {
    key: "fat",
    icon: Flame,
    overline: "Fat",
    kcal: "9 kcal / g",
    body:
      "Dietary fats provide essential fatty acids and assist with functions including cell structure and the absorption of fat-soluble vitamins.",
    note:
      "Because fat contains more than twice the calories per gram of protein or carbohydrate, portion size can have a significant effect on total calorie intake.",
  },
];

function ScienceContent() {
  return (
    <div className="space-y-16" data-testid="macros-content-science">
      {/* Intro */}
      <div className="max-w-3xl">
        <p className="dta-overline text-navy">Give me the science</p>
        <h3 className="font-display uppercase font-bold text-3xl md:text-5xl mt-3 leading-[0.95]">
          Macronutrients explained.
        </h3>
        <p className="mt-6 text-[#272727] text-base md:text-lg leading-relaxed">
          Macronutrients are nutrients required by the body in relatively large quantities. The three principal energy-providing macronutrients are{" "}
          <strong className="text-[#0A0A0A]">protein, carbohydrate and fat</strong>. Each contributes differently to energy intake and physiological function.
        </p>
      </div>

      {/* 3 macro cards (scientific) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {SCIENCE_CARDS.map((c) => {
          const Icon = c.icon;
          return (
            <article
              key={c.key}
              id={`macros-${c.key}`}
              data-testid={`macros-card-science-${c.key}`}
              className="bg-white border border-black/10 p-7 md:p-8 flex flex-col scroll-mt-28"
            >
              <div className="flex items-center gap-3">
                <Icon className="w-5 h-5 text-[#031D47]" strokeWidth={1.5} />
                <p className="dta-overline text-navy">{c.overline}</p>
              </div>
              <p className="mt-5 text-sm md:text-[0.95rem] text-[#272727] leading-relaxed">
                {c.body}
              </p>
              <div className="mt-6 border-t border-black/10 pt-5">
                <p className="dta-overline text-[0.65rem] text-[#272727]/60">
                  Energy density
                </p>
                <p className="font-display uppercase font-bold text-2xl md:text-3xl text-[#031D47] mt-2">
                  {c.kcal}
                </p>
              </div>
              <p className="mt-6 pt-5 border-t border-black/10 text-sm text-[#272727]/85 italic leading-relaxed">
                {c.note}
              </p>
            </article>
          );
        })}
      </div>

      {/* Example calculation */}
      <div className="bg-[#031D47] text-white p-8 md:p-12">
        <p className="dta-overline text-white/50">Calories still matter</p>
        <h3 className="font-display uppercase font-bold text-3xl md:text-5xl mt-3 leading-[0.95]">
          Your macros make up<br />your calories.
        </h3>
        <ul className="mt-10 space-y-4 max-w-xl">
          {[
            { line: "200g Protein × 4 kcal", total: "800 kcal" },
            { line: "300g Carbohydrate × 4 kcal", total: "1,200 kcal" },
            { line: "80g Fat × 9 kcal", total: "720 kcal" },
          ].map((r) => (
            <li
              key={r.line}
              className="flex items-baseline justify-between gap-4 border-b border-white/15 pb-3"
            >
              <span className="text-white/85 text-base md:text-lg">{r.line}</span>
              <span className="font-display uppercase font-bold text-xl md:text-2xl text-white">
                {r.total}
              </span>
            </li>
          ))}
          <li className="flex items-baseline justify-between gap-4 pt-3">
            <span className="dta-overline text-white/60">Total</span>
            <span className="font-display uppercase font-bold text-2xl md:text-3xl">
              ≈ 2,720 kcal
            </span>
          </li>
        </ul>
        <p className="mt-10 max-w-2xl text-white/70 text-base leading-relaxed">
          DTA uses your details and activity level to estimate daily energy requirements and then creates a calorie and macronutrient target appropriate to your selected goal.
        </p>
      </div>

      {/* Goal cards */}
      <div>
        <p className="dta-overline text-navy">Goals explained</p>
        <h3 className="font-display uppercase font-bold text-2xl md:text-3xl mt-3">
          How goals shape the target.
        </h3>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            {
              h: "Fat loss",
              b: "Generally requires consuming less energy than the body uses over time while maintaining sufficient protein and nutrition.",
            },
            {
              h: "Maintenance",
              b: "Aims to broadly balance energy intake with energy expenditure.",
            },
            {
              h: "Muscle gain",
              b: "Generally combines resistance training, sufficient protein and adequate total energy, often using a controlled calorie surplus.",
            },
          ].map((g) => (
            <div key={g.h} className="bg-white border border-black/10 p-6">
              <h4 className="font-display uppercase font-bold text-xl md:text-2xl text-[#0A0A0A]">
                {g.h}
              </h4>
              <p className="mt-3 text-sm text-[#272727] leading-relaxed">{g.b}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Consistency */}
      <div className="bg-[#0A0A0A] text-white p-8 md:p-14 grain">
        <p className="dta-overline text-white/50">Consistency</p>
        <h3 className="hero-headline text-4xl md:text-6xl mt-4 leading-[0.95]">
          Consistency beats<br />perfection.
        </h3>
        <div className="mt-8 max-w-2xl space-y-4 text-white/75 text-base md:text-lg leading-relaxed">
          <p>
            Your body doesn't reset at midnight because you missed a macro target by ten grams. Look at the bigger picture.
          </p>
          <p>
            A single meal will not determine your progress. Neither will one bad day. Repeated habits are what matter.
          </p>
          <p>Follow your targets consistently. Train appropriately. Monitor what happens.</p>
          <p className="font-display uppercase tracking-wide text-white text-lg md:text-xl pt-2">
            Down. Test. Adjust.
          </p>
        </div>
      </div>

      {/* Final message */}
      <div className="max-w-3xl">
        <p className="dta-overline text-navy">In summary</p>
        <h3 className="font-display uppercase font-bold text-3xl md:text-5xl mt-3 leading-[0.95]">
          Calculate. Follow.<br />Measure. Adjust.
        </h3>
        <div className="mt-6 space-y-4 text-[#272727] text-base md:text-lg leading-relaxed">
          <p>
            Your macro calculation isn't a promise of an exact outcome. It gives you a structured starting point.
          </p>
          <p>
            Individual results will vary according to factors including adherence, activity, training, sleep, health and individual physiology.
          </p>
          <p>
            But having a calculated target and following it consistently gives you something far more useful than guessing:
          </p>
          <p className="font-display uppercase font-bold text-2xl md:text-3xl text-[#031D47] pt-2">
            A plan you can measure.
          </p>
        </div>
      </div>
    </div>
  );
}

/* ---------- Composed section ---------- */

export default function WhatAreMacros({ calculatorId = "dta-macro-calculator" }) {
  const [tab, setTab] = useState("simple");

  const handlePick = (key) => {
    // Switch to the tab, then scroll to the chosen macro card
    const el = () => document.getElementById(`macros-${key}`);
    const scroll = () => {
      const node = el();
      if (node) node.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    const node = el();
    if (node) {
      scroll();
    } else {
      // card isn't mounted yet because we may be switching tabs — wait one tick
      requestAnimationFrame(() => requestAnimationFrame(scroll));
    }
  };

  const scrollToCalculator = () => {
    const node = document.getElementById(calculatorId);
    if (node) node.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      className="py-20 md:py-28 bg-[#F4F3EF] border-t border-black/10"
      data-testid="what-are-macros-section"
    >
      <div className="dta-container">
        {/* Section intro */}
        <div className="max-w-3xl">
          <p className="dta-overline text-navy">The basics</p>
          <h2 className="section-heading text-4xl md:text-6xl mt-3 leading-[0.95]">
            What are macros?
          </h2>
          <p className="mt-6 text-[#272727] text-base md:text-lg leading-relaxed">
            Macros don't need to be complicated. "Macros" is short for{" "}
            <strong className="text-[#0A0A0A]">macronutrients</strong> — the nutrients your body needs in relatively large amounts to provide energy, support recovery, maintain normal function and help you work towards your health, fitness and body-composition goals.
          </p>
          <p className="mt-4 text-[#272727] text-base md:text-lg leading-relaxed">
            There are three main macros:
          </p>
          <p className="mt-3 font-display uppercase font-bold text-xl md:text-2xl tracking-wide text-[#031D47]">
            Protein <span className="text-black/30">·</span> Carbohydrates{" "}
            <span className="text-black/30">·</span> Fat
          </p>
          <p className="mt-6 text-[#272727] text-base md:text-lg leading-relaxed">
            Your individual balance of these, together with your overall calorie intake, can be calculated around your body, activity level and goal. Whether you want to lose body fat, maintain your weight or build muscle, understanding your macros gives you a measurable daily target rather than simply guessing what and how much you should eat.
          </p>
        </div>

        {/* Interactive wheel */}
        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <MacrosWheel onPick={handlePick} />
            <p className="text-center mt-4 dta-overline text-[0.65rem] text-[#272727]/60">
              Hover to pause · Tap a segment to explore it
            </p>
          </div>
          <div className="lg:col-span-7">
            <p className="dta-overline text-navy">Explore</p>
            <h3 className="font-display uppercase font-bold text-2xl md:text-4xl mt-3 leading-tight">
              Click a segment.<br />Jump to the detail.
            </h3>
            <p className="mt-5 text-[#272727] text-base leading-relaxed max-w-xl">
              The wheel rotates on its own. Hover or tap to steady it, then click a macro to drop straight into its explanation below. Prefer to read it top-to-bottom? Scroll on.
            </p>

            {/* Explanation style toggle */}
            <div className="mt-8">
              <p className="dta-overline text-[0.65rem] text-[#272727]/60">
                Choose your explanation
              </p>
              <div
                role="tablist"
                aria-label="Macros explanation level"
                className="mt-3 inline-flex border border-black/15 bg-white"
                data-testid="macros-explain-tabs"
              >
                <button
                  role="tab"
                  aria-selected={tab === "simple"}
                  data-testid="macros-tab-simple"
                  onClick={() => setTab("simple")}
                  className={`px-5 md:px-6 py-3 dta-overline text-[0.7rem] transition-colors ${
                    tab === "simple"
                      ? "bg-[#031D47] text-white"
                      : "text-[#272727] hover:bg-[#F4F3EF]"
                  }`}
                >
                  Keep it simple
                </button>
                <button
                  role="tab"
                  aria-selected={tab === "science"}
                  data-testid="macros-tab-science"
                  onClick={() => setTab("science")}
                  className={`px-5 md:px-6 py-3 dta-overline text-[0.7rem] transition-colors border-l border-black/15 ${
                    tab === "science"
                      ? "bg-[#031D47] text-white"
                      : "text-[#272727] hover:bg-[#F4F3EF]"
                  }`}
                >
                  Give me the science
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="mt-16 md:mt-20">
          {tab === "simple" ? <SimpleContent /> : <ScienceContent />}
        </div>

        {/* CTA */}
        <div className="mt-16 md:mt-20 bg-white border border-black/10 p-8 md:p-12 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="max-w-xl">
            <div className="flex items-center gap-3">
              <Target className="w-5 h-5 text-[#031D47]" strokeWidth={1.5} />
              <p className="dta-overline text-navy">Your turn</p>
            </div>
            <h3 className="font-display uppercase font-bold text-2xl md:text-4xl mt-3 leading-tight">
              Macros give you the numbers.<br />
              <span className="text-[#031D47]">DTA does the maths.</span>
            </h3>
            <p className="mt-4 text-sm md:text-base text-[#272727]/85 leading-relaxed">
              Consistency gives you the data. DTA helps you make the adjustment.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              data-testid="macros-cta-calculate"
              onClick={scrollToCalculator}
              className="dta-btn dta-btn-primary inline-flex items-center justify-center gap-2"
            >
              Calculate my macros
              <ArrowRight className="w-4 h-4" strokeWidth={1.75} />
            </button>
            <Link
              to="/support"
              className="dta-btn dta-btn-outline-dark inline-flex items-center justify-center gap-2"
            >
              <Scale className="w-4 h-4" strokeWidth={1.5} />
              Support hub
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
